/**
 * Working out WHY a call to the property's card terminal failed.
 *
 * Shift4's UTG runs on a Windows box on the property's own LAN and this
 * browser posts to it directly. Chrome knows whether that failed with
 * ERR_CONNECTION_REFUSED, ERR_CONNECTION_TIMED_OUT or
 * ERR_CERT_AUTHORITY_INVALID - and shows it in the Network tab - but it hands
 * script one indistinguishable `TypeError: Failed to fetch` for all of them.
 * There is no API that exposes the real code. That single fact is why
 * diagnosing these has meant an ops engineer on AnyDesk.
 *
 * So the verdict is reconstructed, in three tiers of decreasing certainty:
 *
 *   1. From configuration alone, with no network access. Mixed content and a
 *      malformed URL are decidable outright.
 *   2. From a CALIBRATED probe. Absolute timings are useless - a refused
 *      connection on a congested LAN can take longer than a TLS rejection on
 *      a quiet one - so we time the real port against a port on the same host
 *      that is almost certainly closed, and compare. That self-calibrates to
 *      whatever the property's network is doing right now.
 *   3. From a reply the UTG actually sent, which needs no inference at all.
 *
 * Tier 2 is inference, and is reported as such. The escape hatch is
 * `terminalUrlToOpen`: opening the address in a tab makes Chrome show the real
 * interstitial, which is the manual step in the ops runbook reduced to a click.
 */

/** Codes the server's verdict catalogue knows. Keep the two in step. */
export const UTG_VERDICT = {
  MIXED_CONTENT_BLOCKED: "MIXED_CONTENT_BLOCKED",
  CONFIG_INVALID_URL: "CONFIG_INVALID_URL",
  CONFIG_LOOPBACK: "CONFIG_LOOPBACK",
  SERVICE_NOT_LISTENING: "SERVICE_NOT_LISTENING",
  SERVICE_OR_CERTIFICATE: "SERVICE_OR_CERTIFICATE",
  PORT_FILTERED: "PORT_FILTERED",
  HOST_UNREACHABLE: "HOST_UNREACHABLE",
  TLS_REJECTED: "TLS_REJECTED",
  PINPAD_NOT_READY: "PINPAD_NOT_READY",
  LANE_CLOSED: "LANE_CLOSED",
  SF_UNKNOWN: "SF_UNKNOWN",
};

/** How long to wait before calling a probe unreachable. */
const PROBE_TIMEOUT_MS = 4000;

/**
 * A TLS rejection has to complete a TCP handshake before it fails, so it costs
 * measurably more than a refusal - but only about 1-2 extra round trips. On a
 * property LAN that is ~5-20ms, and on loopback ~2-5ms.
 *
 * The first version demanded the real port be 60ms slower than a refusal,
 * which no LAN ever produces: TLS_REJECTED was unreachable on every network
 * this runs on, and every certificate problem was reported as a stopped
 * service. The one test covering it used 260ms, a figure no LAN produces
 * either, so the suite was green.
 */
const TLS_SLOWER_MULTIPLIER = 2.5;
const TLS_SLOWER_FLOOR_MS = 6;

/**
 * Below this, a refusal and a TLS rejection are inside timer noise and cannot
 * honestly be told apart - which is the normal case on loopback. Guessing
 * sends the property to restart a service whose certificate is the actual
 * problem, so the ambiguous verdict names both and points at the definitive
 * check instead.
 */
const INDISTINGUISHABLE_BELOW_MS = 10;

/**
 * How long the failing request itself must have run before we stop believing
 * a fast probe failure.
 *
 * Chrome caches connection failures per host:port. Once a real charge has
 * spent a minute timing out against the terminal, the probe's hit on that
 * same host:port returns instantly from that cache without touching the
 * network - while the control port, a fresh random one, is still measured
 * live. The calibration then compares a cached failure against a real one and
 * concludes the terminal port is being actively refused.
 *
 * Observed in preprod: sale timed out after ~75s, probe real=249ms,
 * control=4001ms, verdict SERVICE_NOT_LISTENING - which sends staff to start
 * a Windows service on a machine that is not reachable at all. The same probe
 * run on its own, both legs cold, correctly said HOST_UNREACHABLE.
 *
 * A refusal is a TCP RST: single-digit to low-tens of milliseconds on a LAN.
 * Nothing that took seconds was refused, so 5s is far above any real refusal
 * and far below any real connect timeout.
 */
const PRIOR_ATTEMPT_HUNG_MS = 5000;

/**
 * True when the URL writes a port after the host, including a scheme default.
 *
 * Reads the authority only - everything between "//" and the first "/", "?" or
 * "#", minus any userinfo - so a colon in a path, in userinfo or in a query
 * value cannot masquerade as a port. The trailing "]" of a bracketed IPv6
 * literal fails the test, which is correct: that is a host, not a port.
 */
function hasExplicitPort(utgUrl) {
  const authority = String(utgUrl).split("//")[1];
  if (!authority) return false;
  const beforePath = authority.split(/[/?#]/)[0];
  const userinfoParts = beforePath.split("@");
  const hostPort = userinfoParts[userinfoParts.length - 1] || "";
  return /:\d+$/.test(hostPort);
}

/** Parse a configured UTG URL. Returns null when it is not usable. */
export function parseUtgUrl(utgUrl) {
  if (!utgUrl || typeof utgUrl !== "string") return null;
  try {
    const parsed = new URL(utgUrl.trim());
    if (!parsed.hostname) return null;
    // `new URL("https://h:443/").port` is "" - the parser strips a scheme's
    // default. Rejecting that told a property reverse-proxying the UTG onto
    // 443 that a correct address was invalid, at the one confidence level the
    // UI does not tag "best guess".
    //
    // Decided from the AUTHORITY, not the raw string: testing /:\d+/ against
    // the whole URL accepted "https://h/path:8080", "https://user:1@h/" and
    // "https://h/x?t=12:30" as though they named a port, then probed 443 on a
    // box with nothing there and reported a confident verdict about it.
    if (!parsed.port && !hasExplicitPort(utgUrl)) return null;
    return parsed;
  } catch (err) {
    return null;
  }
}

function isLoopback(hostname) {
  return (
    hostname === "127.0.0.1" ||
    hostname === "localhost" ||
    hostname === "::1" ||
    hostname === "[::1]"
  );
}

/**
 * What can be decided without touching the network.
 *
 * Returns a blocking verdict when the call cannot possibly succeed, so we do
 * not spend four seconds probing an address the browser was never going to
 * dial. Loopback is deliberately NOT blocking: it is correct when the browser
 * runs on the UTG machine itself, and several properties do exactly that. It
 * rides along as a note instead, to be surfaced only if something else fails.
 */
export function preflightUtgConfig(utgUrl, pageProtocol) {
  const protocol =
    pageProtocol ||
    (typeof window !== "undefined" ? window.location.protocol : "https:");
  const parsed = parseUtgUrl(utgUrl);

  if (!parsed) {
    return {
      verdict: UTG_VERDICT.CONFIG_INVALID_URL,
      confidence: "certain",
      notes: [],
    };
  }

  if (protocol === "https:" && parsed.protocol === "http:") {
    // The browser refuses this before a packet moves, and reports it as the
    // same "Failed to fetch" as everything else. Nothing on the property can
    // fix it - the URL in meta is wrong.
    return {
      verdict: UTG_VERDICT.MIXED_CONTENT_BLOCKED,
      confidence: "certain",
      notes: [],
    };
  }

  const notes = [];
  if (isLoopback(parsed.hostname)) notes.push(UTG_VERDICT.CONFIG_LOOPBACK);
  return { verdict: null, confidence: null, notes, parsed };
}

/**
 * Time one request until it settles, however it settles.
 *
 * `mode: "no-cors"` is what makes this usable as a reachability check: the
 * response is opaque and unreadable, but it still RESOLVES when the host is
 * up, the port is open and the certificate is trusted, and still REJECTS when
 * any of those fail. We only need which of the two happened, and how long it
 * took.
 */
async function timedProbe(url, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const started =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  const elapsed = () =>
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    started;

  try {
    await fetch(url, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return { outcome: "responded", ms: elapsed() };
  } catch (err) {
    return {
      outcome: controller.signal.aborted ? "timeout" : "failed",
      ms: elapsed(),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * A port on the same host that is almost certainly closed, used as the
 * control. Drawn from the ephemeral range so it is neither a real service nor
 * one of the ports Chrome blocks outright (which fail by a different
 * mechanism and would poison the comparison).
 */
function calibrationUrl(parsed) {
  // Full ephemeral range. Note this overlaps the Windows RPC dynamic range,
  // so the port is not guaranteed free - which is why a control that
  // RESPONDS invalidates the calibration above rather than being ignored.
  const port = 49152 + Math.floor(Math.random() * 16384);
  return `${parsed.protocol}//${parsed.hostname}:${port}/`;
}

/**
 * Probe the terminal and decide what the failure was.
 *
 * The two probes run together: sequentially, the second would measure a
 * different moment on the network than the first, which is exactly the
 * comparison we are relying on.
 */
export async function probeUtgReachability(utgUrl, options = {}) {
  const timeoutMs = options.timeoutMs || PROBE_TIMEOUT_MS;
  // How long the request that actually failed ran before giving up. Used to
  // decide whether this probe's own timings can be trusted - see
  // PRIOR_ATTEMPT_HUNG_MS.
  const priorAttemptMs = Number(options.priorAttemptMs) || 0;
  const priorAttemptHung = priorAttemptMs >= PRIOR_ATTEMPT_HUNG_MS;
  const preflight = preflightUtgConfig(utgUrl, options.pageProtocol);

  if (preflight.verdict) {
    return { ...preflight, probed: false };
  }

  const parsed = preflight.parsed;
  const [real, control] = await Promise.all([
    timedProbe(`${parsed.protocol}//${parsed.host}/`, timeoutMs),
    timedProbe(calibrationUrl(parsed), timeoutMs),
  ]);

  const timings = {
    real_ms: Math.round(real.ms),
    control_ms: Math.round(control.ms),
  };

  if (real.outcome === "responded") {
    // Host up, port open, certificate trusted. Whatever went wrong was above
    // the transport, so this probe has nothing to say about it.
    return {
      verdict: null,
      confidence: "certain",
      reachable: true,
      notes: preflight.notes,
      timings,
      probed: true,
    };
  }

  if (real.outcome === "timeout") {
    // A control that FAILED fast means the host answered with a TCP RST: the
    // machine is on, on the network, at the configured address. The terminal
    // port timing out while everything else is refused is a firewall dropping
    // that port specifically - a different fix from "the PC is off or its IP
    // changed", which is what HOST_UNREACHABLE tells them to check.
    const hostAnswered = control.outcome === "failed";
    return {
      verdict: hostAnswered
        ? UTG_VERDICT.PORT_FILTERED
        : UTG_VERDICT.HOST_UNREACHABLE,
      confidence: "likely",
      reachable: false,
      notes: preflight.notes,
      timings,
      probed: true,
    };
  }

  if (control.outcome === "responded") {
    // The control port is open, so it measures nothing about a refusal and
    // the comparison below would silently inflate the threshold and suppress
    // a real TLS_REJECTED. Ephemeral ports are not reliably free on a Windows
    // box - the range overlaps Windows RPC. Say we cannot tell.
    return {
      verdict: UTG_VERDICT.SERVICE_OR_CERTIFICATE,
      confidence: "likely",
      reachable: false,
      notes: preflight.notes,
      timings,
      probed: true,
    };
  }

  // Checked BEFORE the threshold, unconditionally. Gated behind it, this could
  // only ever downgrade SERVICE_NOT_LISTENING - never intercept a TLS verdict,
  // which is the direction it exists for. real=9ms control=1ms cleared a 7ms
  // threshold and was reported as a certificate problem at sub-10ms timings,
  // i.e. on exactly the loopback and quiet-LAN case this floor describes.
  if (
    real.ms < INDISTINGUISHABLE_BELOW_MS &&
    control.ms < INDISTINGUISHABLE_BELOW_MS
  ) {
    return {
      verdict: UTG_VERDICT.SERVICE_OR_CERTIFICATE,
      confidence: "likely",
      reachable: false,
      notes: preflight.notes,
      timings,
      probed: true,
    };
  }

  const tlsThreshold = Math.max(
    control.ms * TLS_SLOWER_MULTIPLIER,
    control.ms + TLS_SLOWER_FLOOR_MS
  );

  if (control.outcome === "timeout") {
    // The control hung but the real port failed fast: something is there and
    // actively refusing, on a network that drops rather than refuses.
    //
    // Unless the request that brought us here hung first. Then the fast
    // failure is Chrome replaying a cached connection failure for this
    // host:port, not a refusal, and the only leg measured against the network
    // is the control - which timed out, meaning the host answered nothing.
    // That is HOST_UNREACHABLE, and it is what this same probe reports when
    // run on its own with both legs cold.
    if (priorAttemptHung) {
      return {
        verdict: UTG_VERDICT.HOST_UNREACHABLE,
        confidence: "likely",
        reachable: false,
        notes: preflight.notes,
        timings,
        probed: true,
      };
    }
    return {
      verdict: UTG_VERDICT.SERVICE_NOT_LISTENING,
      confidence: "likely",
      reachable: false,
      notes: preflight.notes,
      timings,
      probed: true,
    };
  }

  if (real.ms > tlsThreshold) {
    // The real port took materially longer to fail than a closed port on the
    // same host, which means the TCP connection was accepted and the failure
    // came after it - a certificate the browser will not accept.
    return {
      verdict: UTG_VERDICT.TLS_REJECTED,
      confidence: "likely",
      reachable: false,
      notes: preflight.notes,
      timings,
      probed: true,
    };
  }

  // Indistinguishable from a closed port: nothing is listening.
  return {
    verdict: UTG_VERDICT.SERVICE_NOT_LISTENING,
    confidence: "likely",
    reachable: false,
    notes: preflight.notes,
    timings,
    probed: true,
  };
}

/**
 * Read a verdict straight out of a UTG reply, when it sent one.
 *
 * This tier needs no inference: the UTG answered and said what was wrong.
 * Returns null when the body carries nothing recognisable.
 */
export function verdictFromUtgResponse(responseBody) {
  const error = responseBody?.result?.[0]?.error;
  if (!error) return null;

  const text = `${error.shortText || ""} ${error.longText || ""}`.toUpperCase();

  if (text.includes("NOT READY") || String(error.primaryCode) === "9955") {
    return UTG_VERDICT.PINPAD_NOT_READY;
  }
  if (text.includes("LANE CLOSED") || text.includes("LANE IS CLOSED")) {
    return UTG_VERDICT.LANE_CLOSED;
  }
  return null;
}

/**
 * The whole diagnosis for one failed terminal call.
 *
 * Order matters: a reply the UTG actually sent outranks anything inferred from
 * timings, and configuration faults outrank a probe of an address that was
 * never dialled.
 */
export async function diagnoseUtgFailure({
  utgUrl,
  error,
  responseBody,
  pageProtocol,
  priorAttemptMs,
}) {
  const fromResponse = verdictFromUtgResponse(responseBody);
  if (fromResponse) {
    return {
      code: fromResponse,
      confidence: "certain",
      source: "gateway_reply",
      terminalUrlToOpen: null,
      timings: null,
      notes: [],
    };
  }

  const probe = await probeUtgReachability(utgUrl, {
    pageProtocol,
    priorAttemptMs,
  });
  const parsed = parseUtgUrl(utgUrl);

  // A REACHABLE terminal must yield no code at all. Consumers branch on
  // `!code` to show "the terminal answered", so returning a code here made
  // that branch dead and a healthy terminal reported "could not be reached" -
  // the feature's main happy path, inverted.
  //
  // Unreachable with no verdict is genuinely unknown: the probe could not say
  // why, and SF_UNKNOWN's steps point at the definitive check.
  const code =
    probe.verdict || (probe.reachable ? null : UTG_VERDICT.SF_UNKNOWN);

  return {
    code,
    confidence: probe.confidence || "likely",
    source: probe.probed ? "probe" : "config",
    // Opening this makes Chrome name the error script cannot read. Only
    // offered when there is an address worth opening.
    terminalUrlToOpen: parsed ? `${parsed.protocol}//${parsed.host}/` : null,
    timings: probe.timings || null,
    notes: probe.notes || [],
  };
}
